import { type ITransformRT2, type IVector2, Matrix23 } from '@seddi/math-v2';
import { type BufferGeometry, type Object3D, type Ray, Vector2 } from '@seddi/three';
import { type GarmentGeometryGfx, filterMeshes } from '@seddi/ui-three-viewer';
import type { RayIntersectionF } from '@seddi/wa-garments-geometry';

import { getInverseTransformMatrix } from '../core/transform2';
import type { MockupLogo } from '../types/mockupLogos';
import { BufferAttributeAccessor2 } from './bufferAttributeAccessor';
import { computeWorldPosition } from './mesh';
import { type TriMeshBvh, build as buildBvh, dispose as disposeBvh } from './meshBvh';
import type { LogoRegion } from './viewerScene';

export type Hit = { logoId: string | undefined; meshId: string | undefined; uv: Vector2 };

type Intersection = RayIntersectionF & { readonly meshId: string };

const compareIntersect = (a: Intersection, b: Intersection) => a.distance - b.distance;

interface IAttributeAccessor2 {
  at(i: number, ref: Vector2): Vector2;
}

type MeshAccessor = {
  readonly index: Uint16Array | Uint32Array;
  readonly uv?: IAttributeAccessor2;
};

type Mesh = {
  readonly accessor: MeshAccessor;
  readonly bvh: TriMeshBvh;
  readonly id: string;
  readonly polygonOffset: number;
};

const disposeMesh = (mesh: Mesh) => {
  if (mesh.bvh) {
    disposeBvh(mesh.bvh);
  }
};

type Collider = {
  readonly accessor: MeshAccessor;
  readonly bvh: TriMeshBvh;
  readonly id: string;
  readonly regions: string[];
  logos: MockupLogo[];
  readonly polygonOffset: number;
};

const disposeMeshes = (meshes: Mesh[]) => {
  for (const mesh of meshes) {
    disposeMesh(mesh);
  }
  meshes.length = 0;
};

const intersect = (collider: Collider, ray: Ray, intersects: Intersection[] = []) => {
  const UNDEFINED_IDX = 4294967295;
  const result = collider.bvh.pIntersector.intersect(ray.origin, ray.direction, 1e-2, 1e2);

  if (result.triangle !== UNDEFINED_IDX) {
    intersects.push({
      distance: result.distance + collider.polygonOffset,
      meshId: collider.id,
      triangle: result.triangle,
      u: result.u,
      v: result.v,
    });
    return true;
  }
  return false;
};

const isPointInsideLogo = (() => {
  const half = new Vector2();
  const lm = new Matrix23();
  const lp = new Vector2();

  return (size: IVector2, transform: ITransformRT2, p: IVector2): boolean => {
    half.set(size.x * 0.5, size.y * 0.5);
    getInverseTransformMatrix(transform, lm);
    lp.set(p.x, p.y);
    lm.transformRef(lp);
    if (Math.abs(lp.x) > half.x || Math.abs(lp.y) > half.y) {
      return false;
    }
    return true;
  };
})();

export class Raycaster {
  readonly hit: Hit;

  private readonly colliders: Collider[];
  private logos: Readonly<MockupLogo[]>;
  private regions: Readonly<LogoRegion[]>;
  private garmentGeometry: Mesh[] | undefined;
  private readonly models: Map<Readonly<Object3D>, Mesh[]>;

  constructor() {
    this.colliders = [];
    this.hit = {
      logoId: undefined,
      meshId: undefined,
      uv: new Vector2(),
    };
    this.logos = [];
    this.regions = [];
    this.models = new Map();
  }

  dispose() {
    this.colliders.length = 0;
    this.logos = [];
    this.regions = [];

    if (this.garmentGeometry) {
      disposeMeshes(this.garmentGeometry);
      this.garmentGeometry = undefined;
    }

    for (const [, meshes] of this.models) {
      disposeMeshes(meshes);
    }
    this.models.clear();
  }

  async buildGarmentGeometry(garmentGeometry: GarmentGeometryGfx) {
    const accel: Mesh[] = await Promise.all(
      Array.from(garmentGeometry.pieces, async ([id, piece]) => {
        const index = piece.getIndex()!.array as Uint32Array;
        const position = piece.getAttribute('position').array as Float32Array;
        const uv = piece.getAttribute('uv')!;
        const bvh = await buildBvh(index, position);
        const accessor: MeshAccessor = {
          index,
          uv: {
            at: (i: number, ref: Vector2): Vector2 => {
              ref.x = uv.getX(i);
              ref.y = uv.getY(i);
              return ref;
            },
          },
        };
        return { accessor, bvh, id, polygonOffset: 0 };
      }),
    );
    this.garmentGeometry = accel;
    this.initCollidersFromGarmentGeometry();
    this.updateLogosInternal();
  }

  async buildModel(model: Readonly<Object3D>) {
    const modelMeshes = filterMeshes(model);

    const accel: Mesh[] = await Promise.all(
      modelMeshes.map(async mesh => {
        const geometry = mesh.geometry as BufferGeometry;
        const index = geometry.index!.array as Uint16Array | Uint32Array;
        const position = computeWorldPosition(mesh as any);
        const bvh = await buildBvh(index, position);
        const uv = geometry.getAttribute('uv');
        const accessor: MeshAccessor = {
          index,
          uv: uv ? new BufferAttributeAccessor2(uv) : undefined,
        };
        const polygonOffset = (mesh.userData as any)?.isMeshDecal ? -1e-5 : 0;
        const name = ((mesh.userData as any)?.name ?? mesh.name) as string;
        return { accessor, bvh, id: name, polygonOffset };
      }),
    );
    this.models.set(model, accel);
    this.initCollidersFromModel(accel);
    this.updateLogosInternal();
  }

  updateLogos(logos: Readonly<MockupLogo[]>) {
    this.logos = logos;
    this.updateLogosInternal();
  }

  updateRegions(regions: Readonly<LogoRegion[]>) {
    this.colliders.length = 0;
    this.regions = regions;
    this.initCollidersFromGarmentGeometry();
    this.initMeshesFromModels();
    this.updateLogosInternal();
  }

  intersectLogos = (() => {
    const intersects: Intersection[] = [];
    const uv0 = new Vector2();
    const uv1 = new Vector2();
    const uv2 = new Vector2();

    return (ray: Ray): Hit => {
      const { colliders, hit } = this;

      if (!colliders.length) {
        hit.logoId = undefined;
        hit.meshId = undefined;
        return hit;
      }

      intersects.length = 0;
      for (const collider of colliders) {
        intersect(collider, ray, intersects);
      }

      intersects.sort(compareIntersect);

      if (!intersects.length) {
        hit.logoId = undefined;
        hit.meshId = undefined;
        return hit;
      }

      const is = intersects[0];
      hit.logoId = undefined;
      hit.meshId = is.meshId;

      const collider = colliders.find(c => c.id === is.meshId)!;
      const { index, uv } = collider.accessor;

      if (uv) {
        const i0 = index[is.triangle * 3];
        const i1 = index[is.triangle * 3 + 1];
        const i2 = index[is.triangle * 3 + 2];
        uv.at(i0, uv0).multiplyScalar(1.0 - is.u - is.v);
        uv.at(i1, uv1).multiplyScalar(is.u);
        uv.at(i2, uv2).multiplyScalar(is.v);
        uv0.add(uv1).add(uv2);
        hit.uv.copy(uv0);

        for (const logo of collider.logos) {
          if (isPointInsideLogo(logo.size, logo.transform, uv0)) {
            hit.logoId = logo.id;
            return hit;
          }
        }
      }

      return hit;
    };
  })();

  private initCollidersFromGarmentGeometry() {
    const { colliders, garmentGeometry: meshes, regions } = this;

    if (!meshes) return;

    const pieceToRegions: Record<string, string[]> = {};
    for (const region of regions) {
      const entityId = region.entityId;
      pieceToRegions[entityId] ??= [];
      pieceToRegions[entityId].push(region.id);
    }

    for (const mesh of meshes) {
      const { accessor, bvh, id, polygonOffset } = mesh;
      const regionIds = pieceToRegions[id] ?? [];
      colliders.push({
        accessor,
        bvh,
        id: crypto.randomUUID(),
        regions: regionIds,
        logos: [],
        polygonOffset,
      });
    }
  }

  private initCollidersFromModel(meshes: Mesh[]) {
    const { colliders, regions } = this;

    const meshToRegions: Record<string, string[]> = {};
    for (const region of regions) {
      const meshId = region.pieceName;
      meshToRegions[meshId] ??= [];
      meshToRegions[meshId].push(region.id);
    }

    for (const mesh of meshes) {
      const { accessor, bvh, id, polygonOffset } = mesh;
      const regionIds = meshToRegions[id] ?? [];
      colliders.push({
        accessor,
        bvh,
        id: crypto.randomUUID(),
        regions: regionIds,
        logos: [],
        polygonOffset,
      });
    }
  }

  private initMeshesFromModels() {
    for (const [, mesh] of this.models) {
      this.initCollidersFromModel(mesh);
    }
  }

  private updateLogosInternal() {
    const { colliders, logos } = this;

    for (const collider of colliders) {
      const { regions } = collider;
      collider.logos = logos.filter(l => regions.includes(l.region));
    }
  }
}
